import { commonClient, type NearbySportEvent } from '@bip/api-client';
import { toSlug } from '@bip/shared';
import type { NearbyContext, NearbyFetchResult } from '../nearby.types';

/**
 * `dateOfEvent` is `DD/MM/YYYY`, `timeOfEvent` an `HH:mm`-ish string —
 * same combination `NearbySportCard`'s `formatEventDate` parses. Falls
 * back to `Infinity` (sorts last) on a malformed date rather than
 * throwing, since this only reorders an already-fetched list.
 */
function eventTimestamp(event: NearbySportEvent): number {
  const [dd, mm, yyyy] = event.dateOfEvent.split('/');
  const d = new Date(`${yyyy}-${mm}-${dd}T${event.timeOfEvent}`);
  const ms = d.getTime();
  return Number.isNaN(ms) ? Infinity : ms;
}

/**
 * Nearby-sports fetcher. Goes through the common-service cross-sell hub
 * (`GET /bip-api/common/nearby/sports`), which fans out to
 * `sports-events-service /nearby-sports` server-side. Same response
 * shape as before the hub refactor — the card component reads it
 * unchanged.
 */
export async function fetchNearbySports(ctx: NearbyContext): Promise<NearbyFetchResult<NearbySportEvent>> {
  // `ctx.dateFrom` is ISO (YYYY-MM-DD); the hub converts to legacy
  // DD/MM/YYYY before forwarding to sports-events-service.
  const res = await commonClient.getNearbySports({
    latitude: ctx.latitude,
    longitude: ctx.longitude,
    distance: 50,
    // Default fetch limit is 10 (bumped from 4). The strip's Explore
    // button surfaces whenever `items.length > 4`, so the extra rows
    // give the "there's more to browse" signal room to fire without
    // forcing an Explore click just to see a 5th tile. Callers can
    // still override via `ctx.limit`.
    limit:10,
    sortBy: 'distance',
    sortOrder: 'asc',
    ...(ctx.dateFrom ? { dateFrom: ctx.dateFrom } : {}),
    ...(ctx.userCurrency ? { userCurrency: ctx.userCurrency } : {}),
  });

  if (!res.success || !res.data?.length) return { items: [], total: 0 };

  const firstCity = res.data[0]?.city;
  const exploreHref = firstCity ? `/sports/city/${toSlug(firstCity.name, firstCity.id)}` : '/sports';

  // Backend sorts by distance only (`sortBy` never branches on the wire
  // — see `EventsRepository.findNearbySports`) — re-sort client-side so
  // the soonest event shows first, matching the "whichever sport is
  // first, show its card first" requirement.
  const items = [...res.data].sort((a, b) => eventTimestamp(a) - eventTimestamp(b));

  return { items, total: res.meta.totalRecords, exploreHref };
}
